/**
 * Simulator Module — Auto-generate paper trades
 *
 * Entry: myStage OR optStage = CONFIRMED, entryAllowed = true, liquidity OK
 * Risk: capital default 500k PHP, 1% risk/trade, max 3 positions, max 40% exposure
 * Exit: stop loss, confirmed distribution, forced exit, time stop
 */
export function computeSimulatorTrades(signals, priceData, settings) {
  const empty = {
    trades: [],
    equityCurve: [],
    metrics: { winRate: 0, expectancy: 0, avgWin: 0, avgLoss: 0, maxDrawdown: 0, totalTrades: 0 },
  };
  if (!signals.length || !priceData.length) return empty;

  const capital = settings.simCapital || 500000;
  const riskPct = settings.riskPerTrade || 0.01;
  const maxPos = settings.maxPositions || 3;
  const maxExp = settings.maxExposure || 0.4;
  const slPct = settings.stopLossPercent || 0.05;
  const timeDays = settings.timeStopDays || 20;

  const priceBySymbol = {};
  priceData.forEach(r => {
    if (!priceBySymbol[r.symbol]) priceBySymbol[r.symbol] = [];
    priceBySymbol[r.symbol].push(r);
  });

  // Collect all dates from signals' daily data
  const allDatesSet = new Set();
  signals.forEach(s => {
    (s.dailyData || []).forEach(d => allDatesSet.add(d.date));
  });
  const allDates = [...allDatesSet].sort((a, b) => new Date(a) - new Date(b));

  if (!allDates.length) return empty;

  let equity = capital;
  const trades = [];
  const openPos = [];
  const curve = [{ date: allDates[0], equity }];

  // Build signal lookup by symbol and date
  const signalMap = {};
  signals.forEach(s => {
    (s.dailyData || []).forEach((d, idx) => {
      const key = `${s.symbol}_${d.date}`;
      signalMap[key] = {
        ...d,
        symbol: s.symbol,
        myStage: s.myStage,
        optStage: s.optStage,
        entryAllowed: s.entryAllowed,
        liquidityScore: d.liquidityScore,
        exitMode: s.exitMode,
      };
    });
  });

  for (const date of allDates) {
    // Check exits first
    for (let i = openPos.length - 1; i >= 0; i--) {
      const pos = openPos[i];
      const dayPrice = priceBySymbol[pos.symbol]?.find(p => p.date === date);
      if (!dayPrice) continue;

      const curr = dayPrice.close;
      const daysHeld = allDates.indexOf(date) - allDates.indexOf(pos.entryDate);

      // Stop loss
      if (curr <= pos.stopPrice) {
        closeTrade(pos, date, curr, 'STOP_LOSS');
        continue;
      }

      // Distribution exit
      const sig = signalMap[`${pos.symbol}_${date}`];
      if (sig && sig.instNet < 0 && sig.sellerRatio > 1 && sig.accelerationScore < 0) {
        closeTrade(pos, date, curr, 'DISTRIBUTION');
        continue;
      }

      // Time stop
      if (daysHeld >= timeDays) {
        closeTrade(pos, date, curr, 'TIME_STOP');
        continue;
      }
    }

    function closeTrade(pos, exitDate, exitPrice, reason) {
      const pnl = (exitPrice - pos.entryPrice) * pos.shares;
      equity += pnl;
      trades.push({ ...pos, exitDate, exitPrice, pnl, exitReason: reason });
      const idx = openPos.indexOf(pos);
      if (idx > -1) openPos.splice(idx, 1);
    }

    // Check entries
    if (openPos.length < maxPos) {
      const totalExposure = openPos.reduce((s, p) => s + p.entryPrice * p.shares, 0);

      for (const signal of signals) {
        if (openPos.length >= maxPos) break;
        if (totalExposure / Math.max(equity, 1) >= maxExp) break;
        if (openPos.some(p => p.symbol === signal.symbol)) continue;

        const isConfirmed = signal.myStage === 'CONFIRMED' || signal.optStage === 'CONFIRMED';
        if (!isConfirmed || !signal.entryAllowed) continue;
        if ((signal.liquidityScore || 0) < 0.3) continue;

        const dayPrice = priceBySymbol[signal.symbol]?.find(p => p.date === date);
        if (!dayPrice) continue;

        const entryPrice = dayPrice.close;
        const stopPrice = entryPrice * (1 - slPct);
        const riskAmt = equity * riskPct;
        const riskPerShare = entryPrice - stopPrice;
        if (riskPerShare <= 0) continue;
        const shares = Math.floor(riskAmt / riskPerShare);
        if (shares <= 0) continue;

        openPos.push({ symbol: signal.symbol, entryDate: date, entryPrice, stopPrice, shares });
      }
    }

    // MTM equity
    const mtm = openPos.reduce((s, p) => {
      const dp = priceBySymbol[p.symbol]?.find(pr => pr.date === date);
      return s + ((dp?.close || p.entryPrice) - p.entryPrice) * p.shares;
    }, 0);
    curve.push({ date, equity: Math.round(equity + mtm) });
  }

  // Metrics
  const closed = trades;
  const wins = closed.filter(t => t.pnl > 0);
  const losses = closed.filter(t => t.pnl <= 0);
  const winRate = closed.length > 0 ? Math.round((wins.length / closed.length) * 100) : 0;
  const avgWin = wins.length > 0 ? Math.round(wins.reduce((s, t) => s + t.pnl, 0) / wins.length) : 0;
  const avgLoss = losses.length > 0 ? Math.round(losses.reduce((s, t) => s + t.pnl, 0) / losses.length) : 0;
  const expectancy = closed.length > 0 ? Math.round(closed.reduce((s, t) => s + t.pnl, 0) / closed.length) : 0;

  let peak = capital, maxDD = 0;
  for (const pt of curve) {
    if (pt.equity > peak) peak = pt.equity;
    const dd = (peak - pt.equity) / peak;
    if (dd > maxDD) maxDD = dd;
  }

  return {
    trades,
    equityCurve: curve,
    metrics: {
      winRate,
      expectancy,
      avgWin,
      avgLoss,
      maxDrawdown: Math.round(maxDD * 100),
      totalTrades: closed.length,
    },
  };
}
